/**
 * The damage pipeline (TRD-05 §6).
 *
 * Eleven stages in a fixed order. Synergies (TRD-08) insert at named stages and nothing
 * may reorder them: multiplication and subtraction do not commute, so moving armor above
 * the crit multiplier silently rebalances every tower in the game and invalidates every
 * stored replay.
 *
 * Two decisions worth restating because they look like details and are not:
 *
 * Damage is floored to an integer with a minimum of 1 (R-05-18). Integers keep damage
 * numbers readable, and — the real reason — they stop float accumulation from drifting
 * two machines apart over 90 000 ticks.
 *
 * Deaths are MARKED here and resolved in system 10 (R-05-20). A kill that ran its onKill
 * effects inline would mutate the enemy array while `projectileMovement` is still
 * iterating it. That is the single most common source of the bug that works in testing
 * and fails when two things die on the same tick.
 */
import type { Enemy, Tower } from '../entities.js';
import type { SimEvent } from '../events.js';
import type { Rng } from '../rng.js';
import type { DamageKind, EntityId } from '../types.js';
import { vulnerabilityMultiplier } from './status.js';

export interface DamageInput {
  amount: number;
  kind: DamageKind;
  /** Pre-rolled by the projectile that carried it, so a crit survives its travel time. */
  crit: boolean;
  /** Attribution for `damageDealt`; absent for poison and other sourceless damage. */
  tower?: Tower | undefined;
  /**
   * Presentation-only attribution for damage with no shooter — a poison tick belongs to
   * whoever applied the stack.
   *
   * Separate from `tower` on purpose. `tower` also sets `lastHitBy`, which decides whose
   * on-kill hooks fire and who collects the bounty, and moving that to the poisoner
   * would be a real behaviour change dressed up as a counter fix. This credits
   * `damageDealt`/`assistDealt` and nothing else.
   */
  credit?: Tower | undefined;
}

export interface DamageResult {
  /** What was actually subtracted, after every reduction. Never below 1. */
  dealt: number;
  killed: boolean;
}

/**
 * Stages 1–4: everything that happens before the damage meets a specific target.
 *
 * Split out because it runs once at fire time, not once per enemy hit — an AoE shot
 * rolls one crit and applies the same base to everything in the radius, which is both
 * cheaper and what players expect from a single explosion.
 */
export function rollOutgoingDamage(tower: Tower, rng: Rng): { amount: number; crit: boolean } {
  const star = tower.star;
  const scaling = tower.starScaling[star - 1] ?? 1;

  let amount = tower.damage * scaling; // 1. base
  amount += tower.mods.flatDamage; // 2. flat adds
  amount *= tower.mods.damageMultiplier; // 3. multipliers

  // 4. crit. Rolled even at zero chance would waste a draw and shift every subsequent
  // roll in the run, so `chance` short-circuits at p <= 0 — see Rng.chance.
  const crit = rng.chance(tower.mods.critChance);
  if (crit) amount *= tower.mods.critMultiplier;

  return { amount, crit };
}

/**
 * Stages 5–11 against one target.
 *
 * `events` is appended to rather than returned so a single AoE burst produces its hits in
 * radius-query order without an intermediate array per enemy.
 */
/**
 * Is a shot from (x, y) arriving inside the enemy's frontal arc?
 *
 * Pure dot product, deliberately: `cos(theta) >= frontalCos` is the same statement as
 * `dot >= frontalCos * |facing| * |offset|`, and squaring both sides removes the square
 * roots as well. No trigonometry and no `Math.sqrt` — the determinism contract forbids the
 * first, and the second is an engine-dependent last bit nobody needs here.
 *
 * The `dot > 0` guard is load-bearing: squaring loses the sign, so without it a shot from
 * directly BEHIND satisfies the squared inequality just as well as one from the front and
 * the plate would protect the enemy's back.
 */
export function isFrontalHit(enemy: Enemy, x: number, y: number): boolean {
  if (enemy.frontalCos <= 0) return false;

  const dx = x - enemy.x;
  const dy = y - enemy.y;
  const offsetLenSq = dx * dx + dy * dy;
  if (offsetLenSq === 0) return false;

  const dot = enemy.facingX * dx + enemy.facingY * dy;
  if (dot <= 0) return false;

  const facingLenSq = enemy.facingX * enemy.facingX + enemy.facingY * enemy.facingY;
  if (facingLenSq === 0) return false;

  return dot * dot >= enemy.frontalCos * enemy.frontalCos * facingLenSq * offsetLenSq;
}

export function applyDamage(
  enemy: Enemy,
  input: DamageInput,
  tick: number,
  events: SimEvent[],
): DamageResult {
  if (!enemy.alive) return NO_DAMAGE;
  // Still rising out of the ground: the entrance is not a free damage window, or a
  // stacked board simply deletes a boss during its own reveal.
  if (tick < enemy.emergeUntilTick) return NO_DAMAGE;

  let amount = input.amount;

  // 5. vulnerability — Chill and Mark both make the target take more.
  amount *= vulnerabilityMultiplier(enemy, tick);

  // 5b. Void's identity: a slice of the target's max HP, added BEFORE mitigation so it
  //     is still worth having against the armoured enemies Void is meant to answer.
  if (input.tower && input.tower.mods.percentMaxHpDamage > 0) {
    amount += enemy.maxHp * input.tower.mods.percentMaxHpDamage;
  }

  // 6/7. armor and shields. `true` damage skips both outright, which is what lets poison
  // answer the armoured enemies Venom is supposed to counter (R-05-24).
  if (input.kind !== 'true') {
    if (input.kind === 'physical') {
      const pierce = input.tower ? input.tower.mods.armorPierce : 0;
      // Flat subtraction, not a percentage (R-05-19). Flat armor is what makes "small
      // hits do nothing, big hits are fine" legible to a player reading damage numbers.
      const armor = enemy.armor * (1 - pierce);
      amount -= armor > 0 ? armor : 0;
    }
    if (enemy.shieldPerHit > 0) amount -= enemy.shieldPerHit;
  }

  // 7b. The frontal plate (playtest request). A hit arriving within the arc lands for a
  //     fraction of its damage.
  //
  //     Only a hit with a SHOOTER can be deflected: poison and the other sourceless
  //     damage have no direction to arrive from, so a plate cannot turn them. That is a
  //     real counter rather than an oversight — an enemy you cannot get behind is one you
  //     can still rot.
  const deflected = input.tower !== undefined && isFrontalHit(enemy, input.tower.x, input.tower.y);
  if (deflected) amount *= 1 - enemy.frontalReduction;

  // 8. floor, minimum 1.
  const dealt = Math.max(1, Math.floor(amount));

  // 9. apply. A raised shield takes the hit INSTEAD of health — not first, instead.
  //    Whatever is left of a hit that empties the bar dies with it, so a single shot
  //    bigger than the whole shield still only spends one shot's worth of it.
  //
  //    Measured with `power-curve prism`, that waste is only a few percent at ordinary
  //    hit sizes — it bites when a single hit is a real fraction of the whole bar, not
  //    for every heavy gun. Worth knowing before treating it as a balance lever.
  const onShield = enemy.maxShieldHp > 0 && enemy.shieldHp > 0;
  if (onShield) {
    enemy.shieldHp -= dealt;
    if (enemy.shieldHp <= 0) {
      enemy.shieldHp = 0;
      enemy.shieldDownUntilTick = tick + enemy.shieldDownTicks;
      events.push({
        t: 'enemyShieldBroken',
        enemy: enemy.id,
        x: enemy.x,
        y: enemy.y,
        untilTick: enemy.shieldDownUntilTick,
      });
    }
  } else {
    enemy.hp -= dealt;
  }
  const attributed = input.tower ?? input.credit;
  if (attributed) {
    attributed.damageDealt += dealt;
    // B-12 stretch — every aura carrier buffing the dealer is credited with the full
    // hit as an ASSIST. Presentation-only, exactly like damageDealt: never hashed,
    // never read by a rule, so it cannot become a determinism liability.
    const assists = attributed.assistSources;
    for (let i = 0; i < assists.length; i++) {
      const source = assists[i];
      if (source && source.alive) source.assistDealt += dealt;
    }
  }
  if (input.tower) {
    // Attribution is recorded on EVERY attributed hit, not only the fatal one.
    //
    // Damage-over-time has no tower attached — poison ticks arrive as sourceless `true`
    // damage. If this were only set on the killing blow, a Venom board would attribute
    // none of its kills, and Venom-6's own on-death burst could never fire on the deaths
    // Venom actually causes. Keeping the last shooter on record bridges that gap.
    enemy.lastHitBy = input.tower.id;
  }
  events.push({
    t: 'enemyDamaged',
    enemy: enemy.id,
    x: enemy.x,
    y: enemy.y,
    amount: dealt,
    kind: input.kind,
    crit: input.crit,
    /** Turned by the frontal plate — the renderer shows a deflection, not a wound. */
    deflected,
    // So the renderer can tell a hit that rang off the shield from one that drew blood.
    // Events are not hashed, so carrying it costs nothing (TRD-00 §7).
    shielded: onShield,
  });

  // 10. onHit effects are the caller's job — status application needs the tower's hooks,
  //     which this function deliberately does not reach into.

  // 11. death check. The `alive` guard is what makes R-05-21 hold: a Venom burst and a
  //     Void execute landing on the same tick must not both award the bounty.
  if (enemy.hp <= 0 && enemy.alive) {
    enemy.hp = 0;
    enemy.alive = false;
    return { dealt, killed: true };
  }

  return { dealt, killed: false };
}

const NO_DAMAGE: DamageResult = { dealt: 0, killed: false };

/** Convenience for callers that need a stable "who did this" id in an event. */
export function attributionOf(tower: Tower | undefined): EntityId | undefined {
  return tower ? tower.id : undefined;
}
